import { clearAllCredentials } from '@/lib/rms_v2/credentialManager'
import { clearInvestmentProfileStorage } from '@/lib/investmentProfileStorage'
import { clearLegacyEventMapSaves } from '@/queries/event_map/eventMapLegacySaves'
import { ANALYTICS_TRACKING_KEY } from '@/types/analytics_monitor.type'
import { COMPANY_PAGE_CACHE_TRACKING_KEY } from '@/constants/company_page'
import { TREND_V2_TRACKING_KEY } from '@/constants/trend_v2'
import { PENDING_SHAREPOINT_TENANT_KEY } from '@/constants/sharepointConsent'

// Everything user-bound or confidential that the app keeps in browser storage
// is cleared here, so that user sign-out and session expiry remove exactly the
// same set. Add new persistent keys here, not at the call sites.
// Connector sign-in state the OAuth round trip leaves in sessionStorage
// (useConnectorLogin): PKCE verifier, CSRF state, connector and return flag.
const CONNECTOR_LOGIN_KEYS = [
  'codeVerifier',
  'authState',
  'connectorType',
  'connectorReturnToConnectorsTab',
]

// Pending background-job trackers in localStorage (ConversationList treats the
// three as one group) and the per-conversation trend completion flags.
const JOB_TRACKER_KEYS = [
  ANALYTICS_TRACKING_KEY,
  COMPANY_PAGE_CACHE_TRACKING_KEY,
  TREND_V2_TRACKING_KEY,
]
const TREND_COMPLETION_PREFIX = 'trend_v2_completion_'

// Left in place on purpose: `component-state:<conversationId>` (analytics chat
// view state, not confidential — the inventory lists it as view state).

// One throwing storage call (private mode, blocked storage) must neither skip
// the rest nor reach the caller.
const safely = (fn: () => void): void => {
  try {
    fn()
  } catch {
    // storage unavailable — nothing to clear
  }
}

const removeByPrefix = (storage: Storage, prefix: string): void => {
  const keys: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key && key.startsWith(prefix)) keys.push(key)
  }
  keys.forEach((key) => storage.removeItem(key))
}

export const clearSessionStorage = (): void => {
  safely(clearAllCredentials) // connector tokens (sessionStorage; legacy localStorage copies too)
  safely(clearInvestmentProfileStorage)
  safely(clearLegacyEventMapSaves)
  safely(() => {
    for (const key of JOB_TRACKER_KEYS) window.localStorage.removeItem(key)
    window.localStorage.removeItem(PENDING_SHAREPOINT_TENANT_KEY) // SharePoint consent round trip
    removeByPrefix(window.localStorage, TREND_COMPLETION_PREFIX)
    for (const key of CONNECTOR_LOGIN_KEYS) window.sessionStorage.removeItem(key)
  })
}
